import type { ComponentSelector } from "@emotion/react";
import { css } from "@emotion/react";

import { Headline, SponsorHeadline } from "scmp-app/components/content/content-headline/styles";

/**
 * Applied to the element whose box is the visible card frame, to make the whole card
 * clickable. That's usually a variant's Container, but not always — e.g.
 * home-super-splash-large-primary-v2 and home-super-splash-live-primary-v2 apply it to
 * Content instead, because their Container is `display: contents` and never paints a
 * box of its own.
 *
 * The element must generate a box: `display: contents` disqualifies it. position:
 * relative is inert on a box-less element, so the overlay's containing block escapes
 * upward to the next positioned ancestor instead of staying on this element. This
 * exact mistake shipped into review on both super-splash primaries above before being
 * caught and fixed — don't reintroduce it by applying this to a `display: contents`
 * element.
 *
 * Pairs with clickableFrameLinkStyles, which puts an absolutely positioned ::after
 * over the card on the card's primary (headline) link. Every other descendant link and
 * button is raised above that overlay so it keeps its own destination — the comment
 * link, section label, series short label and PLUS tag all stay reachable.
 *
 * The hover rule is required rather than cosmetic: hovering the overlay marks the
 * anchor as :hover, not the Headline nested inside it, so the rules in
 * withHighlightedHeadline never fire on blank space. Colour matches
 * content-item-render/highlight/hoc.tsx.
 */
export const clickableFrameStyles = (frameLink: ComponentSelector) => css`
  position: relative;

  &:hover {
    ${Headline}:not([data-skip-highlight="true"]),
    ${SponsorHeadline}:not([data-skip-highlight="true"]) {
      color: #2c4692;
    }
  }

  a:not(${frameLink}),
  button {
    position: relative;
    z-index: 1;
  }
`;

/**
 * Applied to the card's primary (headline) link — the one passed to
 * clickableFrameStyles. Where a variant reuses one styled anchor for both headline and
 * cover image, scope this to the headline instance or the card gets two overlays.
 *
 * position: static is load-bearing, not tidying. An absolutely positioned element
 * resolves against its nearest non-static ancestor, and a pseudo-element's host is its
 * own parent — so if the frame anchor stays position: relative, inset: 0 covers the
 * headline box instead of the card and the frame is not clickable.
 *
 * That has a cost worth knowing before reaching for it: forcing position: static also
 * removes the anchor as containing block for any of its own absolutely positioned
 * descendants, which then resolve against the frame container instead. This is why
 * scoping matters beyond avoiding a duplicate overlay — home-secondary-v2's cover
 * anchor deliberately keeps position: relative instead of taking these styles, so it
 * stays the containing block for its video play icon and duration overlay.
 */
export const clickableFrameLinkStyles = css`
  position: static;

  &::after {
    position: absolute;
    inset: 0;
    content: "";
  }
`;
